import { useMediaQuery } from '@mui/material';
import { map as lmap, compose as lcompose } from 'lodash/fp';
import {
  disableOperationsHelpDesk,
  excludeStatus,
  ISaveRequestOutput,
  roleLabel,
  roles,
  useDocumentTitle,
  useRoleOpContext,
  useToggle,
  ValueLabel,
} from '../common';
import {
  RequestStatus2d,
  RequestStatusStack,
  RequestStatusType,
  isRequestStatusTypeForApproval,
  useGetMoreRequestData,
} from '.';

const requestStatusProperties: ValueLabel<keyof ISaveRequestOutput>[] = [
  { value: 'requestId', label: 'Request ID' },
  { value: 'reqType', label: 'Type' },
  { value: 'role', label: 'Role' },
  { value: 'updatedAt', label: 'Updated Date' },
  { value: 'status', label: 'Status' },
  { value: 'additionalInfo', label: 'Additional Information' },
  { value: 'approverComments', label: 'Approver Comments' },
  { value: 'beneficiaryEmail', label: 'Subscriber Information' },
  { value: 'requestorEmail', label: 'Requester' },
];
const approvalRequestStatusProperties: ValueLabel<keyof ISaveRequestOutput>[] =
  [
    { value: 'requestId', label: 'Request Id' },
    { value: 'reqType', label: 'Request Type' },
    { value: 'role', label: 'Role' },
    { value: 'beneficiaryInfo', label: 'Subscriber Information' },
    { value: 'updatedAt', label: 'Updated Date' },
    { value: 'requestorEmail', label: 'Requester' },
  ];
const memberRequestStatusProperties: ValueLabel<keyof ISaveRequestOutput>[] = [
  { value: 'requestId', label: 'Request Id' },
  { value: 'reqType', label: 'Request Type' },
  { value: 'role', label: 'Role' },
  { value: 'updatedAt', label: 'Updated Date' },
  { value: 'status', label: 'Status' },
  { value: 'approverComments', label: 'Approver Comments' },
  { value: 'beneficiaryInfo', label: 'Subscriber Information' },
  { value: 'requestorEmail', label: 'Requester' },
];

export function RequestStatus({
  requests,
  requestStatusType,
  now,
  selectedRequests,
  toggleRequestSelection,
  confirmCancelRequests,
}: {
  requests: ISaveRequestOutput[];
  requestStatusType: RequestStatusType;
  now?: Date;
  selectedRequests?: ISaveRequestOutput[];
  toggleRequestSelection?: (payload: ISaveRequestOutput) => void;
  confirmCancelRequests?: () => void;
}) {
  useDocumentTitle({ title: 'Status of Requests' });

  const matchesGridBreakpoint = useMediaQuery('(min-width:60em)', {
    noSsr: true,
  });

  const preprocess = lcompose(
    lmap<ISaveRequestOutput, ISaveRequestOutput>(a => ({
      ...a,
      role: roleLabel(roles)(a.role || a.reqRole || ''),
    })),
    excludeStatus(['NEW'])
  );

  // requests have to loaded before RequestStatus
  // otherwise, toggle will not work
  const { value: expandRequestValue, toggle } = useToggle({
    init: lmap<ISaveRequestOutput, ValueLabel<boolean>>(({ requestId }) => ({
      label: String(requestId),
      value: false,
    }))(requests),
  });

  const { moreRequestData, add, remove } = useGetMoreRequestData({
    requestStatusType,
  });

  const handleExpandRequestValue = (requestId: string) => {
    toggle(requestId);

    !expandRequestValue(requestId) && add(parseInt(requestId, 10));

    expandRequestValue(requestId) && remove(parseInt(requestId, 10));
  };

  const { operations } = useRoleOpContext();
  const disableOperationsHelpDsk = disableOperationsHelpDesk(operations);

  return matchesGridBreakpoint ? (
    <RequestStatus2d
      properties={
        isRequestStatusTypeForApproval(requestStatusType)
          ? approvalRequestStatusProperties
          : requestStatusType === 'MemberRequestStatus'
          ? memberRequestStatusProperties
          : requestStatusProperties
      }
      getRequstsCallStatus={true}
      requests={preprocess(requests)}
      expandRequestValue={expandRequestValue}
      toggleExpandRequest={handleExpandRequestValue}
      moreRequestData={moreRequestData}
      requestStatusType={requestStatusType}
      disableOperationsHelpDsk={disableOperationsHelpDsk}
      now={now}
      selectedRequests={selectedRequests}
      toggleRequestSelection={toggleRequestSelection}
      confirmCancelRequests={confirmCancelRequests}
    />
  ) : (
    <RequestStatusStack
      properties={
        isRequestStatusTypeForApproval(requestStatusType)
          ? approvalRequestStatusProperties
          : requestStatusType === 'MemberRequestStatus'
          ? memberRequestStatusProperties
          : requestStatusProperties
      }
      getRequstsCallStatus={true}
      requests={preprocess(requests)}
      expandRequestValue={expandRequestValue}
      toggleExpandRequest={handleExpandRequestValue}
      moreRequestData={moreRequestData}
      requestStatusType={requestStatusType}
      disableOperationsHelpDsk={disableOperationsHelpDsk}
      now={now}
      selectedRequests={selectedRequests}
      toggleRequestSelection={toggleRequestSelection}
      confirmCancelRequests={confirmCancelRequests}
    />
  );
}
